import { DepartmentTreeNode } from './DepartmentTreeNode';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  IconArrowUp,
  IconChevronDown,
  IconDashboard,
  IconEye,
  IconList,
  IconSearch,
  IconSortAsc,
  IconSortDesc,
} from '@/icons';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import Fuse from 'fuse.js';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetDepartmentPath,
  SortOptions,
} from '@/hooks/useGetDepartmentPath';
import { cn } from '@/helpers/classnames';
import {
  DepartmentTreeNodeDTO,
  useGetCurrentDepartmentTree,
} from '@/api/trafikportalen';
import { SelectedDepartment } from '@/types/selectedDepartment';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import IconButton from '../core/IconButton';
import { collator } from '@/utils/collator';
import { SortDirection } from '@/types/filterSearch';

type ViewMode = 'tree' | 'list';

interface Props {
  selectedDepartmentId?: string;
  mobileView: boolean;
  onChange: (department: SelectedDepartment) => void;
  showFilter?: boolean;
  disabledDepartments?: string[];
  enabledDepartments?: string[];
  icons?: Record<string, ReactNode>;
  initialSortOption?: SortOptions;
  dontHighlightSelectedDepartment?: boolean;
  className?: string;
}

export const DepartmentTree: React.FC<Props> = ({
  selectedDepartmentId,
  mobileView,
  onChange,
  showFilter = true,
  disabledDepartments,
  enabledDepartments,
  icons,
  initialSortOption = 'editable',
  dontHighlightSelectedDepartment,
  className,
}) => {
  const { formatMessage } = useIntl();
  const { data: departmentTree } = useGetCurrentDepartmentTree();
  const [usedDepartmentId, setUsedDepartmentId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const sortDepartmentsTree = useGetDepartmentPath(selectedDepartmentId);
  const [listRef] = useAutoAnimateConditional<HTMLUListElement>();
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(
    new Set(),
  );

  const canEditAllDepartments = useMemo(() => {
    if (!departmentTree) return true;

    let allDepartmentsEditable = true;
    const traverse = (node: DepartmentTreeNodeDTO) => {
      if (!node.admin) {
        allDepartmentsEditable = false;
      }
      node.children.forEach(traverse);
    };
    traverse(departmentTree);

    return allDepartmentsEditable;
  }, [departmentTree]);

  const hasDeepHierachy = useMemo(() => {
    if (!departmentTree) return true;

    const departmentTreePath = sortDepartmentsTree(
      departmentTree as DepartmentTreeNodeDTO,
      selectedDepartmentId,
      'all',
    );

    return departmentTreePath.paths.some((path) => path.length > 2);
  }, [departmentTree, selectedDepartmentId, sortDepartmentsTree]);

  const flattenedDepartments = useMemo(() => {
    if (!departmentTree) return [];

    const flatten = (node: DepartmentTreeNodeDTO): DepartmentTreeNodeDTO[] => {
      if (node.type.startsWith('school-')) {
        return node.children.flatMap(flatten);
      }

      const result = [node];
      node.children.forEach((child) => {
        result.push(...flatten(child));
      });
      return result;
    };

    return flatten(departmentTree);
  }, [departmentTree]);

  const fuse = useMemo(() => {
    return new Fuse(flattenedDepartments, {
      keys: ['name'],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [flattenedDepartments]);

  const filteredDepartments = useMemo(() => {
    if (!searchQuery.trim()) return flattenedDepartments;

    const results = fuse.search(searchQuery.trim());
    return results.map((result) => result.item);
  }, [fuse, searchQuery, flattenedDepartments]);

  const sortedDepartments = useMemo(() => {
    return [...filteredDepartments].sort((a, b) =>
      sortDirection === 'asc'
        ? collator.compare(a.name.toLowerCase(), b.name.toLowerCase())
        : collator.compare(b.name.toLowerCase(), a.name.toLowerCase()),
    );
  }, [filteredDepartments, sortDirection]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  const handleToggleSortByName = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  const handleToggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === 'tree' ? 'list' : 'tree'));
  }, []);

  const handleSortDepartmentTree = useCallback(
    (sortOptions: SortOptions, update?: boolean) => {
      if (!departmentTree) return;

      const departmentTreePath = sortDepartmentsTree(
        departmentTree as DepartmentTreeNodeDTO,
        selectedDepartmentId,
        sortOptions,
      );

      const initializeExpanded =
        departmentTreePath.paths.length > 0 && expandedDepartments.size === 0;

      if (initializeExpanded || update) {
        const parentDepartmentPaths = [...departmentTreePath.paths]
          .reverse()
          .flat();
        setExpandedDepartments(new Set(parentDepartmentPaths));
      }
    },
    [
      departmentTree,
      selectedDepartmentId,
      sortDepartmentsTree,
      expandedDepartments,
    ],
  );

  const findPathToDepartment = useCallback(
    (
      node: DepartmentTreeNodeDTO,
      targetId: string,
      path: string[] = [],
    ): string[] | null => {
      const currentPath = [...path, node.id];
      if (node.id === targetId) return currentPath;
      for (const child of node.children) {
        const found = findPathToDepartment(child, targetId, currentPath);
        if (found) return found;
      }
      return null;
    },
    [],
  );

  const handleToggle = useCallback((department: SelectedDepartment) => {
    setExpandedDepartments((prev) => {
      const next = new Set(prev);
      if (next.has(department.id)) {
        next.delete(department.id);
      } else {
        next.add(department.id);
      }
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (department: SelectedDepartment) => {
      setUsedDepartmentId(department.id);

      if (!expandedDepartments.has(department.id)) {
        handleToggle(department);
      }

      onChange(department);
    },
    [handleToggle, expandedDepartments, onChange],
  );

  useEffect(() => {
    if (
      selectedDepartmentId &&
      selectedDepartmentId !== usedDepartmentId &&
      usedDepartmentId
    ) {
      setUsedDepartmentId(selectedDepartmentId);
    }
  }, [selectedDepartmentId, usedDepartmentId]);

  useEffect(() => {
    if (!departmentTree) return;
    if (!usedDepartmentId) {
      handleSortDepartmentTree(initialSortOption);
      setUsedDepartmentId(selectedDepartmentId ?? '');

      if (selectedDepartmentId) {
        const path = findPathToDepartment(departmentTree, selectedDepartmentId);
        if (path) {
          setExpandedDepartments((prev) => {
            const next = new Set(prev);
            path.forEach((id) => next.add(id));
            return next;
          });
        }
      }
    }
  }, [
    handleSortDepartmentTree,
    departmentTree,
    usedDepartmentId,
    selectedDepartmentId,
    initialSortOption,
    findPathToDepartment,
  ]);

  useEffect(() => {
    if (searchQuery.trim()) {
      setViewMode('list');
    }
  }, [searchQuery]);

  if (!departmentTree) return null;

  return (
    <>
      {showFilter && viewMode === 'tree' && (
        <FilterButtons
          handleSortDepartments={handleSortDepartmentTree}
          canEditAllDepartments={canEditAllDepartments}
          hasDeepHierachy={hasDeepHierachy}
        />
      )}
      <div className="mt-2 flex w-full gap-2 px-2">
        <IconButton
          variant={viewMode === 'list' ? 'filled' : 'outline'}
          onClick={handleToggleViewMode}
          aria-label={
            viewMode === 'tree'
              ? formatMessage({ defaultMessage: 'Listvy' })
              : formatMessage({ defaultMessage: 'Trädvy' })
          }
          icon={viewMode === 'tree' ? <IconList /> : <IconDashboard />}
        />
        <Input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={formatMessage({
            defaultMessage: 'Sök avdelning',
          })}
          startAdornment={<IconSearch />}
        />
        {viewMode === 'list' && (
          <IconButton
            onClick={handleToggleSortByName}
            icon={sortDirection === 'asc' ? <IconSortAsc /> : <IconSortDesc />}
            aria-label={formatMessage(
              {
                defaultMessage: 'Sortera efter namn {sortDirection}',
              },
              {
                sortDirection:
                  sortDirection === 'asc' ? 'stigande' : 'fallande',
              },
            )}
          />
        )}
      </div>
      <ul
        key={viewMode}
        ref={listRef}
        className={cn(
          'hidden w-full flex-col gap-3 p-4 sm:overflow-auto md:flex',
          mobileView && 'flex',
          className,
        )}
      >
        {viewMode === 'list' ? (
          sortedDepartments.map((dept) => {
            const isSelected =
              dept.id === usedDepartmentId && !dontHighlightSelectedDepartment;
            const isDisabled =
              disabledDepartments?.includes(dept.id) ||
              (!!enabledDepartments && !enabledDepartments.includes(dept.id));

            return (
              <li key={dept.id} className="flex w-full">
                <Button
                  className="relative w-full justify-start truncate text-sm font-normal transition-all lg:text-base"
                  variant={isSelected ? 'filled' : 'outline'}
                  onClick={() => handleSelect({ id: dept.id, name: dept.name })}
                  disabled={isDisabled}
                >
                  <p className="mr-auto truncate">{dept.name}</p>
                  <div className="min-w-max">{icons?.[dept.id]}</div>
                </Button>
              </li>
            );
          })
        ) : (
          <DepartmentTreeNode
            departmentNode={departmentTree}
            onSelect={handleSelect}
            onToggle={handleToggle}
            selectedDepartmentId={usedDepartmentId}
            expandedDepartments={expandedDepartments}
            disabledDepartments={disabledDepartments}
            enabledDepartments={enabledDepartments}
            icons={icons}
            dontHighlightSelectedDepartment={dontHighlightSelectedDepartment}
          />
        )}
      </ul>
    </>
  );
};

interface FilterButtonsProps {
  handleSortDepartments: (filterOptions: SortOptions, update?: boolean) => void;
  canEditAllDepartments: boolean;
  hasDeepHierachy: boolean;
}

export const FilterButtons: React.FC<FilterButtonsProps> = ({
  handleSortDepartments,
  canEditAllDepartments,
  hasDeepHierachy,
}) => {
  const filterOptions = [];

  if (hasDeepHierachy) {
    filterOptions.push({
      id: 1,
      label: <FormattedMessage defaultMessage="Samtliga" />,
      icon: <IconChevronDown />,
      option: 'all',
    });
    filterOptions.push({
      id: 3,
      label: <FormattedMessage defaultMessage="Stäng" />,
      icon: <IconArrowUp />,
      option: 'closed',
    });
  }

  if (!canEditAllDepartments) {
    filterOptions.splice(1, 0, {
      id: 2,
      label: <FormattedMessage defaultMessage="Åtkomst" />,
      icon: <IconEye />,
      option: 'editable',
    });
  }

  if (filterOptions.length === 0) return null;

  return (
    <div className="mt-2 flex w-full flex-wrap items-center gap-x-2 px-2">
      {filterOptions.map((option) => (
        <Button
          key={option.id}
          startIcon={option.icon}
          onClick={() =>
            handleSortDepartments(option.option as SortOptions, true)
          }
          variant="ghost"
          className="flex-1"
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
};
